import dayjs from 'dayjs';
import { isNil, merge } from 'lodash-es';
import { i18n } from 'next-i18next';

import type { MyVoucherDto } from '../../../../../../resources/@types/courseSchema';
import { COURSE_URL, INFLEARN_URL } from '../../../../../constants/urls';
import { formatDate, toYYYY_MM_DD_HH_mm } from '../../../../../utils/date';

export function courseCardDataTransformer(data: MyVoucherDto) {
  const courseType = data.course.offlineCourse
    ? 'OFF'
    : data.course.challengeCourse
      ? 'CHALLENGE'
      : 'ON';
  const expireInformation = getExpireInformation(data.limitedAt);
  const dashboardLink = {
    ON: COURSE_URL.COURSE_DASHBOARD(data.course.slug),
    CHALLENGE: COURSE_URL.CHALLENGE_DASHBOARD(data.course.slug),
    OFF: COURSE_URL.OFFLINE_DETAIL(data.course.slug),
  }[courseType];

  const lectureLink = INFLEARN_URL.COURSES_LECTURE({
    courseId: data.course.id,
    unitId: data.unitId,
  });

  // B2B 유저 그룹 신청시
  const isB2BGroupOrder = data.type === 'GROUP_ORDER';

  return {
    ...data,
    voucherId: data.id,
    course: {
      ...data.course,
      courseId: data.course.id,
      isOfflineCourse: !isNil(data.course.offlineCourse),
      isChallengeCourse: !isNil(data.course.challengeCourse),
      offlineCourse: data.course.offlineCourse
        ? merge(data.course.offlineCourse, {
            duration: getOfflineCourseDuration(
              data.course.offlineCourse.startedAt,
              data.course.offlineCourse.endedAt,
            ),
            surveyUrl: INFLEARN_URL.SURVEY(data.course.id),
            surveyReadonlyUrl: INFLEARN_URL.SURVEY_READ_ONLY({
              courseId: data.course.id,
              userId: data.userId,
            }),
          })
        : undefined,
    },
    expireInformation,
    progress: Number(data.progress),
    dashboardLink,
    lectureLink,
    isCancelable: isB2BGroupOrder ? false : !!data.cancelType,
  };
}

/**
 * @return {string} 24.06.27. 09:00 ~ 24.08.06. 17:50 (한국 표준시)
 */
function getOfflineCourseDuration(startDate: string, endDate: string) {
  return `${formatDate(startDate, 'YY.MM.DD. HH:mm')} ~ ${formatDate(endDate, 'YY.MM.DD. HH:mm')}`;
}

function getExpireInformation(date?: string | null): {
  label: string;
  isExpired: boolean;
} {
  if (isNil(date)) {
    return {
      isExpired: false,
      label: i18n?.t('무제한') || '무제한',
    };
  }

  const expireDate = dayjs(date);

  // 현재 시각 만료일 이후
  if (dayjs().isAfter(expireDate)) {
    return {
      label:
        i18n?.t('{{date}} 만료됨', { date: toYYYY_MM_DD_HH_mm(date) }) ||
        `${toYYYY_MM_DD_HH_mm(date)} 만료됨`,
      isExpired: true,
    };
  }

  return {
    label:
      i18n?.t('{{date}} 만료', { date: toYYYY_MM_DD_HH_mm(date) }) ||
      `${toYYYY_MM_DD_HH_mm(date)} 만료`,
    isExpired: false,
  };
}

export type CourseCardData = ReturnType<typeof courseCardDataTransformer>;
